import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/utils/common";

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-base font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 disabled:bg-gray-50 disabled:text-content-disabled",
        secondary:
          "border border-secondary-foreground bg-background text-secondary-foreground hover:bg-secondary/50 hover:text-accent-foreground disabled:border-gray-200 disabled:text-gray-200",
        tertiary:
          "bg-gray-800 text-white hover:bg-gray-800/90 disabled:bg-gray-50 disabled:text-content-disabled",
        outline:
          "border border-gray-600 bg-white text-content-secondary hover:bg-secondary/50 disabled:border-gray-200 disabled:text-gray-200",
        transparent:
          "bg-teal-50 text-secondary-foreground disabled:bg-gray-50 disabled:text-content-disabled",
        text: "bg-white text-content-primary hover:bg-secondary/50 disabled:text-content-disabled",
        quaternary:
          "border border-gray-200 text-content-disabled disabled:border-gray-50 disabled:text-gray-200",
        quinary:
          "border border-secondary-foreground bg-teal-50 text-secondary-foreground hover:bg-teal-50/50 disabled:border-gray-300 disabled:bg-gray-50 disabled:text-content-disabled",
        ghost: "hover:bg-accent hover:text-accent-foreground",
      },
      size: {
        default: "h-[56px] rounded-[20px] px-6 py-4",
        md: "h-[50px] w-fit rounded-[16px] px-5 py-3 text-sm",
        sm: "h-8 w-fit rounded-[12px] px-3 py-1.5 text-sm font-medium",
        icon: "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type="button"
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
